import { AnyAction, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit';
import settingsSlice, { SettingsSliceState } from './features/settings-slice';

import campaignsSlice, { CampaignSliceState } from './features/campaign-slice';
import attendeesSlice, { AttendeeSliceState } from 'src/redesign/features/Attendees/store/reducer';
import campaignLogsSlice, { CampaignLogsSliceState } from './features/campaign-log-slice';
import siteSlice, { SubscriptionSliceState } from './features/site-slice';
import {
  attendeeActivitiesSlice,
  AttendeeActivitiesState,
} from 'src/redesign/features/AttendeesSidebar/views/ActivitiesSidebarView/store/reducer';
import { attendeeSidebarSlice } from 'src/redesign/features/AttendeesSidebar/store/reducer';
import {
  AttendeeSidebarState,
  EmailDeliveryState,
} from 'src/redesign/features/AttendeesSidebar/types';
import { emailDeliverySlice } from 'src/redesign/features/AttendeesSidebar/tabs/EmailDelivery/store/reducer';
import { toastNotificationsSlice } from 'src/redesign/features/Toast/store/reducer';
import { ToastNotificationsState } from 'src/redesign/features/Toast/types';
import { loginSlice } from 'src/redesign/pages/Login/store/reducer';
import { AuthState } from 'src/redesign/pages/Login/types';
import { accountSlice } from 'src/redesign/features/Account/store/reducer';
import { AccountSliceState } from 'src/redesign/features/Account/store/types';
import { integrationsSlice } from 'src/redesign/pages/IntegrationsPage/store/reducer';
import { IntegrationsState } from 'src/redesign/pages/IntegrationsPage/types';
import { modalsSlice } from 'src/redesign/features/Modals/store/reducer';
import { ModalsState } from 'src/redesign/features/Modals/types';
import { selectedSiteSlice } from 'src/redesign/features/SelectedSite/store/reducer';
import { SelectedSiteState } from 'src/redesign/features/SelectedSite/types';
import { eventsSlice } from 'src/redesign/features/Events/store/reducer';
import { EventsSliceState } from 'src/redesign/features/Events/types';
import { eventsFiltersSlice } from 'src/redesign/features/EventsFilters/reducer';
import { EventsFiltersState } from 'src/redesign/features/EventsFilters/types';
import { attendeeStatsSlice } from 'src/redesign/features/AttendeeStats/reducer';
import { AttendeeStatsState } from 'src/redesign/features/AttendeeStats/types';
import { attendeeEventsSidebarSlice } from 'src/redesign/features/AttendeeEventsSidebar/store/reducer';
import { AttendeeEventsSidebarState } from 'src/redesign/features/AttendeeEventsSidebar/store/types';
import { sitesSlice } from 'src/redesign/features/Sites/reducer';
import { SitesState } from 'src/redesign/features/Sites/types';
import { widgetsSlice } from 'src/redesign/features/Widgets/reducer';
import { WidgetState } from 'src/redesign/features/Widgets/types';
import { listenerMiddleware } from 'src/redesign/features/Attendees/store/listeners';
import { GlobalSliceState } from 'src/types/types';
import { globalSlice } from 'src/redesign/shared/store/reducer';

const rootReducer = combineReducers({
  [eventsSlice.name]: eventsSlice.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [attendeesSlice.name]: attendeesSlice.reducer,
  [campaignsSlice.name]: campaignsSlice.reducer,
  [campaignLogsSlice.name]: campaignLogsSlice.reducer,
  [accountSlice.name]: accountSlice.reducer,
  [siteSlice.name]: siteSlice.reducer,
  [attendeeActivitiesSlice.name]: attendeeActivitiesSlice.reducer,
  [attendeeSidebarSlice.name]: attendeeSidebarSlice.reducer,
  [emailDeliverySlice.name]: emailDeliverySlice.reducer,
  [toastNotificationsSlice.name]: toastNotificationsSlice.reducer,
  [loginSlice.name]: loginSlice.reducer,
  [integrationsSlice.name]: integrationsSlice.reducer,
  [modalsSlice.name]: modalsSlice.reducer,
  [selectedSiteSlice.name]: selectedSiteSlice.reducer,
  [eventsFiltersSlice.name]: eventsFiltersSlice.reducer,
  [attendeeStatsSlice.name]: attendeeStatsSlice.reducer,
  [attendeeEventsSidebarSlice.name]: attendeeEventsSidebarSlice.reducer,
  [sitesSlice.name]: sitesSlice.reducer,
  [widgetsSlice.name]: widgetsSlice.reducer,
  [globalSlice.name]: globalSlice.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredPaths: ['payload.config.transformRequest'],
      },
    }).prepend(listenerMiddleware.middleware),
  //devTools: process.env.NODE_ENV !== 'production',
});
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = {
  global: GlobalSliceState;
  events: EventsSliceState;
  campaigns: CampaignSliceState;
  campaignLogs: CampaignLogsSliceState;
  attendees: AttendeeSliceState;
  settings: SettingsSliceState;
  account: AccountSliceState;
  sites: SubscriptionSliceState;
  attendeeActivities: AttendeeActivitiesState;
  attendeesSidebar: AttendeeSidebarState;
  emailDelivery: EmailDeliveryState;
  toastNotifications: ToastNotificationsState;
  login: AuthState;
  integrations: IntegrationsState;
  modals: ModalsState;
  selectedSite: SelectedSiteState;
  eventsFilters: EventsFiltersState;
  attendeeStats: AttendeeStatsState;
  attendeeEventsSidebar: AttendeeEventsSidebarState;
  sitesSlice: SitesState;
  widgets: WidgetState;
};
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, any, unknown, AnyAction>;
